/**
 * ClinicContext — org context for the top-of-sidebar clinic switcher.
 *
 * ## The switch is a safety-critical sequence, not a state update
 *
 * The Dexie cache (patients, visits, prescriptions, the push queue) is
 * single-clinic by construction, and most local reads do not filter by
 * clinic_id — `db.patients.toArray()` in StartVisitPage is literally every
 * patient on the device. That is correct today only because the database can
 * only ever hold one clinic's data. Switching clinics deletes that invariant,
 * so the switch itself has to restore it:
 *
 *   1. Refuse to switch while offline (we can neither drain nor re-hydrate).
 *   2. Drain pending offline writes UNDER THE OLD CLINIC. Skipping this does
 *      not lose the writes — it files them against the NEW clinic, which RLS
 *      accepts because the session is legitimately scoped there. That is
 *      cross-clinic data corruption and it is invisible afterwards.
 *   3. Authorize + audit server-side before touching anything local.
 *   4. Flip the header, THEN flush. Flushing last means the wipe is the final
 *      thing to touch stale rows, so a pull that lands mid-switch cannot leave
 *      clinic A data behind.
 *   5. Re-hydrate for the new clinic.
 *   6. Hard reload to /dashboard.
 *
 * Step 6 is not laziness. Flushing IndexedDB does not clear React state:
 * without a reload, every mounted page keeps rendering the patient lists and
 * dashboards it already fetched from clinic A. A reload guarantees no clinic-A
 * state survives in memory, and resetting the route matters too — a patient
 * detail page from clinic A would 404 (or worse, look empty) under clinic B.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Hub } from "aws-amplify/utils";

import { clearPhiTables } from "../db/security-lifecycle";
import { ApiError } from "../services/api.errors";
import {
  AccessibleClinic,
  OrgContext,
  OrganizationAPI,
} from "../services/organization.service";
import { pendingCount } from "../services/offline-sync.service";
import { pushOnce } from "../services/sync-push.engine";
import { runBootstrap } from "../sync/bootstrap-engine";
import {
  announceClinicScopeChange,
  getActiveClinicId,
  getClinicScopeTarget,
  isClinicScopeStale,
  onClinicScopeChangedElsewhere,
  setActiveClinicId,
  setHomeClinicId,
} from "../utils/active-clinic";
import {
  cacheBelongsToAnotherClinic,
  getCacheClinicId,
  setCacheClinicId,
} from "../utils/cache-scope";
import { logger } from "../utils/logger";

export class ClinicSwitchBlockedError extends Error {
  constructor(message: string, readonly reason: "offline" | "unsynced") {
    super(message);
    this.name = "ClinicSwitchBlockedError";
  }
}

/**
 * Branches in one group usually share a prefix and differ only by locality, so
 * the full name repeated in every sentence of the switch overlay is noise. Uses
 * `location` when the backend has one, else the tail after an em/en dash or
 * hyphen, else the whole name.
 */
export function shortClinicName(clinic: AccessibleClinic | null): string {
  if (!clinic) return "this clinic";
  if (clinic.location?.trim()) return clinic.location.trim();
  const tail = clinic.name.split(/\s+[—–-]\s+/).pop();
  return (tail ?? clinic.name).trim() || clinic.name;
}

/**
 * Guess the organisation name from a clinic that is about to gain a sibling.
 *
 * "Mehta Clinic — Rajouri Garden" (locality "Rajouri Garden") becomes "Mehta
 * Clinic"; "Sinchan Clinic" with an unrelated locality stays as it is, which
 * reads perfectly well as the name of the group it heads.
 *
 * Derived rather than prompted for. At add-a-clinic time this string is only a
 * label above the switcher — cosmetic, and changeable — so a decent guess plus
 * an easy rename beats interrupting with a concept ("organisation") the user
 * has not thought about yet. The name that actually matters is the legal entity
 * on a combined invoice, and that is worth asking for properly at the point
 * roll-up billing is enabled, alongside GSTIN and address.
 *
 * Uses the clinic's own `location` instead of a blind regex, so it only strips
 * a suffix it can positively identify as the locality.
 */
export function deriveOrganizationName(clinic: AccessibleClinic | null): string {
  if (!clinic) return "";
  const name = clinic.name.trim();
  const locality = clinic.location?.trim();
  if (!locality) return name;

  const lowered = name.toLowerCase();
  if (!lowered.endsWith(locality.toLowerCase())) return name;

  const head = name.slice(0, name.length - locality.length);
  // Drop a trailing separator left behind by the locality, e.g. " — " or ", ".
  const trimmed = head.replace(/[\s—–\-,|/]+$/u, "").trim();
  return trimmed || name;
}

export type SwitchStepId = "drain" | "authorize" | "flush" | "hydrate";
export type SwitchStepStatus = "pending" | "active" | "done" | "failed";

export interface SwitchStep {
  id: SwitchStepId;
  /** Phrased in the past tense — it is read once the tick is showing. */
  label: string;
  status: SwitchStepStatus;
}

export interface SwitchProgress {
  toName: string;
  toShortName: string;
  fromShortName: string | null;
  steps: SwitchStep[];
  /** Set when a step failed; the overlay stays up so the user can read it. */
  error: string | null;
}

interface ClinicContextValue {
  isOrgOwner: boolean;
  organizationName: string | null;
  clinics: AccessibleClinic[];
  activeClinicId: string | null;
  activeClinic: AccessibleClinic | null;
  /** True only when there is genuinely somewhere else to go. */
  canSwitch: boolean;
  /** Live connectivity. Switching clinics requires it. */
  isOnline: boolean;
  isLoading: boolean;
  /** True while a switch is mid-flight. False once it has failed and stopped. */
  isSwitching: boolean;
  /** Step-by-step state for the switch overlay. Null when no switch is shown. */
  switchProgress: SwitchProgress | null;
  /**
   * Set once ANOTHER tab moves this browser to a different clinic. This tab is
   * then locked until it reloads: the shared cache no longer holds the clinic
   * it is showing. `toClinic` is null when that clinic is not in our list.
   */
  staleTab: { toClinic: AccessibleClinic | null } | null;
  switchClinic: (clinicId: string) => Promise<void>;
  /** Login-time clinic choice. See enterClinic for why it is not a switch. */
  enterClinic: (clinicId: string) => Promise<void>;
  /** Dismiss a FAILED switch overlay. A successful switch ends in a reload. */
  dismissSwitch: () => void;
  refresh: () => Promise<void>;
  /** Fetch the org context if this session does not have it yet. */
  ensureLoaded: () => void;
}

const ClinicContext = createContext<ClinicContextValue>({
  isOrgOwner: false,
  organizationName: null,
  clinics: [],
  activeClinicId: null,
  activeClinic: null,
  canSwitch: false,
  isOnline: true,
  isLoading: true,
  isSwitching: false,
  switchProgress: null,
  staleTab: null,
  switchClinic: async () => {},
  enterClinic: async () => {},
  dismissSwitch: () => {},
  refresh: async () => {},
  ensureLoaded: () => {},
});

export const ClinicProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [ctx, setCtx] = useState<OrgContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Non-null for the whole duration of a switch, and afterwards too if it
  // failed — the overlay owns its own dismissal in that case.
  const [progress, setProgress] = useState<SwitchProgress | null>(null);
  const [activeClinicId, setActive] = useState<string | null>(
    getActiveClinicId(),
  );
  // Switching is online-only, so the UI needs to reflect connectivity live —
  // showing an enabled control that always fails on click is worse than
  // showing a disabled one that explains itself.
  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine);
  const [changedElsewhere, setChangedElsewhere] = useState(false);

  useEffect(
    () => onClinicScopeChangedElsewhere(() => setChangedElsewhere(true)),
    [],
  );

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Mirrors ctx for ensureLoaded, which must not re-create on every fetch.
  const ctxRef = useRef<OrgContext | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  // Bumped on sign-out: a fetch begun for the previous user must not land.
  const session = useRef(0);

  const refresh = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    const startedFor = session.current;
    const run: Promise<void> = (async () => {
      try {
        const next = await OrganizationAPI.getContext();
        if (session.current !== startedFor) return;
        ctxRef.current = next;
        setCtx(next);
        setIsLoading(false);
        // The user's own clinic, whichever clinic any tab is working in. Lets
        // api.client leave X-Active-Clinic off when it would only name home.
        setHomeClinicId(next.home_clinic_id);
        // Another tab may have switched while this request was in flight. Its
        // choice is now the browser's clinic; this tab's older answer (the
        // clinic IT sent) must not write over it.
        if (isClinicScopeStale()) return;
        // Trust the server's answer over local storage. It has already applied
        // org-membership validation, so this self-heals a stale stored id.
        setActiveClinicId(next.active_clinic_id);
        setActive(next.active_clinic_id);
      } catch (err) {
        if (session.current !== startedFor) return;
        // No session yet: the provider mounts on the login page, and a device
        // may still be mid-verification. Nothing is wrong, there is just no
        // one to answer for — stay loading, and ensureLoaded fetches again
        // once the user is inside the app.
        if (
          err instanceof ApiError &&
          (err.isUnauthorized || err.message === "device_verification_required")
        ) {
          return;
        }
        // Non-fatal: a failure here just means no switcher renders. Never block
        // the app — a receptionist who cannot reach this endpoint must still be
        // able to work in their own clinic.
        logger.warn("org context fetch failed", {
          error_name: err instanceof Error ? err.name : "Unknown",
        });
        setIsLoading(false);
      }
    })().finally(() => {
      if (inFlight.current === run) inFlight.current = null;
    });
    inFlight.current = run;
    return run;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Signing in navigates into the app without a page load, so the fetch made
  // when this provider mounted answered for whoever was signed in THEN —
  // usually nobody. The clinic app calls this on entry; it is a no-op once the
  // context is loaded.
  const ensureLoaded = useCallback(() => {
    if (ctxRef.current) return;
    setIsLoading(true);
    void refresh();
  }, [refresh]);

  // Sign-out is SPA-only too. Drop this organization's clinics so the next
  // person to sign in on this tab never sees them.
  useEffect(
    () =>
      Hub.listen("auth", ({ payload }) => {
        if (payload.event !== "signedOut") return;
        session.current += 1;
        inFlight.current = null;
        setHomeClinicId(null);
        ctxRef.current = null;
        setCtx(null);
        setIsLoading(true);
      }),
    [],
  );

  // The backend rejected our stored active clinic (moved out of the org, or
  // deactivated). api.client has already dropped the id; re-resolve so the UI
  // and the local cache stop reflecting a clinic we can no longer reach.
  useEffect(() => {
    const onInvalid = () => {
      setActive(null);
      // The wipe below empties the cache every other tab is reading.
      announceClinicScopeChange(null);
      void refresh();
      clearPhiTables();
    };
    window.addEventListener("kaagazy:active-clinic-invalid", onInvalid);
    return () =>
      window.removeEventListener("kaagazy:active-clinic-invalid", onInvalid);
  }, [refresh]);

  const switchClinic = useCallback(
    async (clinicId: string) => {
      if (clinicId === activeClinicId) return;

      const target = (ctx?.clinics ?? []).find((c) => c.clinic_id === clinicId);
      const from =
        (ctx?.clinics ?? []).find((c) => c.clinic_id === activeClinicId) ?? null;

      // ── 1. Connectivity ────────────────────────────────────────────────
      // Offline we can neither drain the queue under the old clinic nor pull
      // the new one's data — the owner would land in an empty cache with no
      // way to fill it. Rejected before any progress UI appears: nothing has
      // started, so there is nothing to narrate.
      if (!navigator.onLine) {
        throw new ClinicSwitchBlockedError(
          "You need to be online to switch clinics.",
          "offline",
        );
      }

      const toShortName = shortClinicName(target ?? null);
      const fromShortName = from ? shortClinicName(from) : null;

      // Only advertise the drain step when there is actually something to
      // drain. Listing a step that instantly ticks teaches the user to ignore
      // the list; the common case should show exactly the work being done.
      const pendingBefore = await pendingCount();

      const steps: SwitchStep[] = [
        ...(pendingBefore > 0
          ? [
              {
                id: "drain" as const,
                label: `Synced ${pendingBefore} pending record${
                  pendingBefore === 1 ? "" : "s"
                } to ${fromShortName ?? "the current clinic"}`,
                status: "pending" as const,
              },
            ]
          : []),
        {
          id: "authorize",
          label: "Server confirmed you own this clinic",
          status: "pending",
        },
        {
          id: "flush",
          label: "Local cache cleared — patients, visits, appointments, queue",
          status: "pending",
        },
        { id: "hydrate", label: `Loading ${toShortName} workspace`, status: "pending" },
      ];

      setProgress({
        toName: target?.name ?? "the selected clinic",
        toShortName,
        fromShortName,
        steps,
        error: null,
      });

      // Advance the named step and mark everything before it done. Driven by
      // real completions, never by a timer — a fake progress animation here
      // would claim the cache was cleared before it was.
      const mark = (id: SwitchStepId, status: SwitchStepStatus) =>
        setProgress((prev) =>
          prev
            ? {
                ...prev,
                steps: prev.steps.map((s) =>
                  s.id === id ? { ...s, status } : s,
                ),
              }
            : prev,
        );

      const fail = (message: string, id: SwitchStepId) =>
        setProgress((prev) =>
          prev
            ? {
                ...prev,
                error: message,
                steps: prev.steps.map((s) =>
                  s.id === id ? { ...s, status: "failed" } : s,
                ),
              }
            : prev,
        );

      // Lock every other tab of this browser before touching the queue they
      // share. From here on they are showing a clinic the browser is leaving;
      // their next request or cache write would mix the two. This also stops
      // any pull or bootstrap THIS tab already had running for the old clinic.
      announceClinicScopeChange(clinicId);

      try {
        // ── 2. Drain under the OLD clinic ────────────────────────────────
        // Never destroy unsynced work: push it first, and only refuse if it
        // will not go. `clearPhiTables` below wipes sync_queue, so anything
        // still pending at that point would be gone for good.
        if (pendingBefore > 0) {
          mark("drain", "active");
          await pushOnce();
          const stillPending = await pendingCount();
          if (stillPending > 0) {
            const message =
              `${stillPending} unsaved record${stillPending === 1 ? "" : "s"} ` +
              `could not be synced. Resolve them before switching clinics — ` +
              `switching now would file them against ${toShortName}.`;
            fail(message, "drain");
            throw new ClinicSwitchBlockedError(message, "unsynced");
          }
          mark("drain", "done");
        }

        // ── 3. Authorize + audit, before touching anything local ─────────
        // A 403 here leaves the device untouched. Doing the flush first would
        // wipe the cache for a switch that then gets refused.
        mark("authorize", "active");
        await OrganizationAPI.setActiveClinic(clinicId);
        mark("authorize", "done");

        // ── 4. Flip the header, then flush ───────────────────────────────
        // Flush last so the wipe is the final operation to touch stale rows.
        mark("flush", "active");
        setActiveClinicId(clinicId);
        setActive(clinicId);
        await clearPhiTables();
        mark("flush", "done");

        // ── 5. Re-hydrate for the new clinic ─────────────────────────────
        // clearPhiTables reset the pull checkpoint and cleared the bootstrap
        // flag, so this is a full pull scoped to the new clinic by RLS.
        mark("hydrate", "active");
        await runBootstrap();
        setCacheClinicId(clinicId);
        mark("hydrate", "done");

        // ── 6. Hard reload ───────────────────────────────────────────────
        // Clears every page's in-memory clinic-A state, and resets the route
        // so we do not land on a record that does not exist in this clinic.
        // The overlay deliberately stays up until the navigation replaces it.
        window.location.assign("/dashboard");
      } catch (err) {
        // Keep the overlay up on failure so the user can read what happened
        // and, critically, WHICH steps already took effect. A switch that died
        // after the flush left the device with an empty cache; silently
        // dropping the overlay would leave that unexplained.
        setProgress((prev) => {
          if (!prev || prev.error) return prev;
          const activeStep = prev.steps.find((s) => s.status === "active");
          return {
            ...prev,
            error:
              err instanceof Error
                ? err.message
                : "Could not switch clinics. Please try again.",
            steps: prev.steps.map((s) =>
              s.id === activeStep?.id ? { ...s, status: "failed" } : s,
            ),
          };
        });
        throw err;
      }
    },
    [activeClinicId, ctx],
  );

  /**
   * Choose a clinic at LOGIN, from the clinic picker.
   *
   * Deliberately lighter than `switchClinic`, because the situations differ:
   *
   *   * A mid-session switch interrupts work. There may be unsynced writes for
   *     the clinic being left, so they are drained first and the switch is
   *     refused if they will not go.
   *   * A login-time choice starts work. Usually the cache is already empty —
   *     sign-out flushes it — so there is nothing to drain and no reason to
   *     reload the app.
   *
   * The exception is a session that ended WITHOUT signing out: closed tab, dead
   * battery. The cache survives holding the previous clinic's PHI, and unfiltered
   * local reads would serve it under the newly chosen clinic's name. So when the
   * cache belongs to a different clinic it is flushed — unless it still holds
   * unsynced writes, in which case we refuse and say which clinic to open to
   * sync them. Flushing would destroy work that exists nowhere else.
   */
  const enterClinic = useCallback(
    async (clinicId: string) => {
      // Same connectivity rule as a mid-session switch, for the same reason:
      // entering a clinic may need to flush and re-fill the local cache, and
      // offline we can do the destroying half but not the refilling half.
      // Landing in an empty clinic with no way to populate it is worse than
      // being told to reconnect.
      if (!navigator.onLine) {
        throw new ClinicSwitchBlockedError(
          "You need to be online to open a different clinic.",
          "offline",
        );
      }

      const cacheIsForeign = cacheBelongsToAnotherClinic(clinicId);
      if (cacheIsForeign) {
        const stranded = await pendingCount();
        if (stranded > 0) {
          const cachedId = getCacheClinicId();
          const owner = (ctx?.clinics ?? []).find(
            (c) => c.clinic_id === cachedId,
          );
          throw new ClinicSwitchBlockedError(
            `This device has ${stranded} unsynced record${stranded === 1 ? "" : "s"} ` +
              `from ${owner ? shortClinicName(owner) : "another clinic"}. ` +
              `Open that clinic first so they can sync.`,
            "unsynced",
          );
        }
      }
      // /select-clinic is reachable mid-session, so another tab may still be
      // working in the clinic being left.
      if (cacheIsForeign || getActiveClinicId() !== clinicId) {
        announceClinicScopeChange(clinicId);
      }
      if (cacheIsForeign) await clearPhiTables();

      setActiveClinicId(clinicId);
      setActive(clinicId);
      // Mark the scope before hydrating. A pull that is interrupted leaves a
      // partial cache, and it must still be attributed to THIS clinic — the
      // alternative (marking afterwards) leaves partial rows labelled as
      // belonging to the previous clinic.
      setCacheClinicId(clinicId);
    },
    [ctx],
  );

  const dismissSwitch = useCallback(() => setProgress(null), []);

  const clinics = ctx?.clinics ?? [];
  const activeClinic =
    clinics.find((c) => c.clinic_id === activeClinicId) ??
    clinics.find((c) => c.is_home) ??
    null;
  const staleTab = changedElsewhere
    ? {
        toClinic:
          clinics.find((c) => c.clinic_id === getClinicScopeTarget()) ?? null,
      }
    : null;

  return (
    <ClinicContext.Provider
      value={{
        isOrgOwner: ctx?.is_org_owner ?? false,
        organizationName: ctx?.organization?.name ?? null,
        clinics,
        activeClinicId: activeClinicId ?? ctx?.active_clinic_id ?? null,
        activeClinic,
        // Owners with exactly one clinic get no switcher — there is nowhere to
        // switch to, and an inert chevron just invites a confused click.
        canSwitch: (ctx?.is_org_owner ?? false) && clinics.length > 1,
        isOnline,
        isLoading,
        // A failed switch keeps its overlay up but is no longer in flight, so
        // controls must re-enable — otherwise the user is stuck behind a
        // disabled UI with no way to retry.
        isSwitching: progress !== null && progress.error === null,
        switchProgress: progress,
        staleTab,
        switchClinic,
        enterClinic,
        dismissSwitch,
        refresh,
        ensureLoaded,
      }}
    >
      {children}
    </ClinicContext.Provider>
  );
};

export const useClinic = () => useContext(ClinicContext);
